/**
 * Plan cards under an Alfie answer about Future Builder's packages.
 *
 * Hermes sends the cards (`metadata.planCards`) only on a package answer and only for plans
 * ranked above the member; this component just renders them. Copy is the brochure's
 * (`Future-Builder-Tiers-Brochure`, 29 Jun 2026) via Hermes `PLAN_BROCHURE`, prices are the
 * mirrored catalog's, and the look follows the design canvas Tim approved on 11 Oct 2026.
 *
 * Every button goes to a purchase screen that already exists — nothing here changes them:
 *   buy / selector / application / inquire → `/signup?plan=CODE&email=…`
 *     (signup locks the plan, opens the plan list for Business, the K2K application, or the
 *     Inner Circle inquiry form; a signed-in member is forwarded on to checkout)
 *   contact (Enterprise) → an email to the team
 *   invitation (Founders Table) → no link
 * The email is the signed-in member's, added here so it never travels through the model.
 *
 * Under the cards: a "what happens next" line built from the cards' actions (deterministic,
 * never model text) and a blue underlined text link per card, plus "Compare all plans".
 */
import { type ReactNode, useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { useAuth } from '@/contexts/AuthContext';
import type { PlanAction, PlanCardData } from '@/api/alfie';
import { type PlanCardSurface, trackPlanCard } from './plan-card-analytics';

/** The existing Enterprise contact link on the plan selector (SignupPlanSelectStep). */
export const ENTERPRISE_MAILTO = 'mailto:support@futurebuilder.co?subject=Enterprise%20plan';

/** The brochure's global terms line. */
const TERMS_LINE = 'All prices AUD inc. GST · 12-month minimum commitment.';

/** Actions whose button opens the signup flow with the plan locked in. */
const SIGNUP_ACTIONS: ReadonlySet<PlanAction> = new Set([
  'buy',
  'selector',
  'application',
  'inquire',
]);

const BLUE_LINK =
  'font-semibold text-[#1D5FD1] underline underline-offset-2 hover:text-[#164CA8] dark:text-[#7CB4FF] dark:hover:text-[#A9CDFF]';

function nextStepSentence(card: PlanCardData): string {
  const name = card.copy.name;
  // A card with no destination that is not an invitation: an add-on, which cannot be bought
  // in the app yet. Never promise a checkout or form that is not there.
  if (!card.linkable && card.action !== 'invitation' && card.action !== 'none') {
    return `${name} can't be bought here yet, so ask the team through Contact us on Home.`;
  }
  switch (card.action) {
    case 'buy':
      return `${name} goes straight to checkout.`;
    case 'selector':
      return `${name} opens the plan list so you can pick it.`;
    case 'application':
      return `${name} opens the application, and the team reviews it.`;
    case 'inquire':
      return `${name} opens a short inquiry and the team comes back to you.`;
    case 'invitation':
      return `${name} is by invitation, so speak with Luke or your coach.`;
    case 'contact':
      return `${name} goes to support@futurebuilder.co.`;
    default:
      return '';
  }
}

function textLinkLabel(card: PlanCardData): string {
  if (card.action === 'contact') return `Email us about ${card.copy.name} →`;
  if (card.action === 'inquire') return `Inquire about ${card.copy.name} →`;
  return `${card.copy.ctaLabel ?? `Choose ${card.copy.name}`} →`;
}

/** "+ Alfie Business · up to 20 users" → "Alfie Business". */
function bundledName(pill: string | null): string | null {
  const m = pill?.match(/Alfie (Pro|Business)/);
  return m ? m[0] : null;
}

function splitPrice(label: string): { amount: string; per: string | null } {
  const m = label.match(/^(.*?)(\/mo)$/);
  return m ? { amount: m[1], per: m[2] } : { amount: label, per: null };
}

interface PlanLinkProps {
  card: PlanCardData;
  email: string | undefined;
  className: string;
  children: ReactNode;
  onClick: () => void;
}

/** The destination for a card, or null when it has none (invitation / free). */
function PlanLink({ card, email, className, children, onClick }: PlanLinkProps) {
  if (card.linkable && SIGNUP_ACTIONS.has(card.action)) {
    return (
      <Link
        to="/signup"
        search={{ plan: card.code, ...(email ? { email } : {}) }}
        className={className}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  }
  if (card.linkable && card.action === 'contact') {
    return (
      <a href={ENTERPRISE_MAILTO} className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  return null;
}

function buttonClass(card: PlanCardData): string {
  const base =
    'inline-flex min-h-[44px] w-full items-center justify-center rounded-xl px-4 text-[14px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  switch (card.action) {
    case 'buy':
      return `${base} bg-[#C9561B] text-white hover:bg-[#B04A16]`;
    case 'selector':
      return `${base} border-2 border-[#C9561B] text-[#C9561B] hover:bg-[#C9561B]/10 dark:border-[#FF9A45] dark:text-[#FF9A45]`;
    case 'application':
    case 'inquire':
      return `${base} bg-[#0F0E0D] text-white hover:bg-[#1A1A18] dark:border dark:border-white/20`;
    case 'contact':
      return `${base} border border-border text-foreground hover:bg-foreground/[0.05]`;
    default:
      return `${base} border border-[#E8B887]/50 text-[#E8B887]`;
  }
}

function PlanCard({
  card,
  email,
  surface,
  compact,
}: {
  card: PlanCardData;
  email: string | undefined;
  surface: PlanCardSurface;
  compact: boolean;
}) {
  const { copy } = card;
  const dark = copy.dark;
  const price = splitPrice(card.priceLabel);
  const bundled = copy.includesAlfieFree ? bundledName(copy.pill) : null;
  const click = (via: 'button' | 'text_link') => () =>
    trackPlanCard({ action: 'plan_card_clicked', code: card.code, surface, planAction: card.action, via });

  const surfaceClass = dark
    ? 'bg-[linear-gradient(145deg,#1C1B19,#2A2622)] text-[#EDEBE8] border border-[#E8B887]/30'
    : copy.flagship
      ? 'bg-card text-foreground border-2 border-[#FF8E2C]'
      : 'bg-card text-foreground border border-border';
  const eyebrowClass = dark ? 'text-[#E8B887]' : 'text-[#B4561C] dark:text-[#FF9A45]';
  const subtle = dark ? 'text-[#C9C4BC]' : 'text-muted-foreground';
  const accentText = dark ? 'text-[#E8B887]' : 'text-[#B4561C] dark:text-[#FFB677]';

  return (
    <article
      data-plan={card.code}
      className={`relative flex flex-col gap-3 rounded-2xl p-5 shadow-sm ${surfaceClass} ${
        compact ? 'w-[85%] flex-shrink-0 snap-start' : 'w-[85%] flex-shrink-0 snap-start md:w-auto'
      }`}
    >
      {bundled && (
        <span
          className={`absolute right-4 top-4 -rotate-6 rounded-md border-2 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[.08em] ${
            dark ? 'border-[#7FCF97] text-[#7FCF97]' : 'border-[#2F6B45] text-[#2F6B45] dark:border-[#7FCF97] dark:text-[#7FCF97]'
          }`}
        >
          {bundled} included free
        </span>
      )}
      {copy.badge && (
        <div className={`pr-24 text-[11px] font-extrabold uppercase tracking-[.14em] ${eyebrowClass}`}>
          {copy.badge}
        </div>
      )}
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-[18px] font-bold tracking-[-0.01em]">{copy.name}</h3>
        <span className="text-[34px] font-extrabold leading-none tabular-nums">
          {price.amount}
          {price.per && <span className={`ml-0.5 text-[14px] font-semibold ${subtle}`}>{price.per}</span>}
        </span>
      </div>
      <div
        className={`rounded-xl border px-4 py-3 ${
          dark
            ? 'border-[#E8B887]/35 bg-[#E8B887]/[0.08]'
            : 'border-[rgba(255,142,44,.35)] bg-gradient-to-br from-[rgba(255,142,44,.14)] to-[rgba(255,142,44,.03)]'
        }`}
      >
        <div className="text-[18px] font-extrabold leading-snug">{copy.hero.headline}</div>
        {copy.hero.sub && <div className={`mt-0.5 text-[13px] font-semibold ${accentText}`}>{copy.hero.sub}</div>}
      </div>
      {copy.pill && (
        <span
          className={`self-start rounded-full px-3 py-1 text-[12px] font-semibold ${
            dark ? 'bg-[#E8B887]/15 text-[#E8B887]' : 'bg-[rgba(255,142,44,.12)] text-[#B4561C] dark:text-[#FFB677]'
          }`}
        >
          {copy.pill}
        </span>
      )}
      {(copy.bestFor ?? copy.summary) && (
        <p className={`text-[13px] italic leading-relaxed ${subtle}`}>{copy.bestFor ?? copy.summary}</p>
      )}
      {copy.bullets.length > 0 && (
        <ul className="flex flex-col gap-1.5 text-[13px] leading-snug">
          {copy.bullets.map((b, i) => (
            <li key={i} className="flex gap-2">
              <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#FF8E2C]" />
              <span className={b.startsWith('Everything in') ? 'font-bold' : undefined}>{b}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-auto flex flex-col gap-3 pt-1">
        {copy.seatsLine && (
          <div className={`border-t pt-3 text-[12px] font-medium ${dark ? 'border-white/10' : 'border-border'} ${subtle}`}>
            {copy.seatsLine}
          </div>
        )}
        <PlanLink card={card} email={email} className={buttonClass(card)} onClick={click('button')}>
          {copy.ctaLabel ?? `Choose ${copy.name}`}
        </PlanLink>
        {card.action === 'invitation' && (
          <span className={buttonClass(card)} aria-disabled="true">
            {copy.ctaLabel ?? 'By invitation'}
          </span>
        )}
      </div>
    </article>
  );
}

export function PlanCards({
  cards,
  surface,
  compact = false,
}: {
  cards: PlanCardData[] | undefined;
  surface: PlanCardSurface;
  /** The floating widget: always one swipeable row. */
  compact?: boolean;
}) {
  const { user } = useAuth();
  const email = user?.email ?? undefined;
  const list = (cards ?? []).slice(0, 3);
  const codes = list.map((c) => c.code).join(',');

  // One `shown` row per card per mounted answer.
  const shownRef = useRef<string | null>(null);
  useEffect(() => {
    if (!codes || shownRef.current === codes) return;
    shownRef.current = codes;
    for (const code of codes.split(',')) trackPlanCard({ action: 'plan_card_shown', code, surface });
  }, [codes, surface]);

  if (list.length === 0) return null;

  const pro = list.find((c) => c.code === 'PRO');
  const opensSignup = list.some((c) => c.linkable && SIGNUP_ACTIONS.has(c.action));
  const nextSteps = [
    opensSignup ? 'The button opens your plan page with your email already filled in.' : '',
    ...list.map(nextStepSentence),
    TERMS_LINE,
  ]
    .filter(Boolean)
    .join(' ');
  const textLinks = list.filter((c) => c.linkable && (SIGNUP_ACTIONS.has(c.action) || c.action === 'contact'));
  const gridCols = list.length === 1 ? 'md:grid-cols-1 md:max-w-sm' : list.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3';

  return (
    <section aria-label="Plans" className="mt-4 flex min-w-0 flex-col gap-3">
      {pro && (
        // A PRO card is only offered to a Free member, so "you are here" is the Free step.
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl bg-foreground/[0.04] px-3 py-2 text-[12px] text-muted-foreground">
          <span className="text-[11px] font-extrabold uppercase tracking-[.14em] text-[#B4561C] dark:text-[#FF9A45]">
            How it fits
          </span>
          <span className="font-semibold text-foreground">Free diagnostic · you are here</span>
          <span aria-hidden="true">→</span>
          <span>Alfie Pro {splitPrice(pro.priceLabel).amount} (software + Basecamp)</span>
          <span aria-hidden="true">→</span>
          <span>add Knowledge &amp; Community</span>
        </div>
      )}
      <div
        className={`flex min-w-0 snap-x snap-mandatory gap-3 overflow-x-auto pb-2 scrollbar-hide ${
          compact ? '' : `md:grid md:overflow-visible md:pb-0 ${gridCols}`
        }`}
      >
        {list.map((card) => (
          <PlanCard key={card.code} card={card} email={email} surface={surface} compact={compact} />
        ))}
      </div>
      <div className="flex flex-col gap-2 text-[13px] leading-relaxed text-muted-foreground">
        <p>
          <strong className="font-semibold text-foreground">What happens next:</strong> {nextSteps}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {textLinks.map((card) => (
            <PlanLink
              key={card.code}
              card={card}
              email={email}
              className={BLUE_LINK}
              onClick={() =>
                trackPlanCard({
                  action: 'plan_card_clicked',
                  code: card.code,
                  surface,
                  planAction: card.action,
                  via: 'text_link',
                })
              }
            >
              {textLinkLabel(card)}
            </PlanLink>
          ))}
          <Link to="/signup" search={email ? { email } : {}} className={BLUE_LINK}>
            Compare all plans →
          </Link>
        </div>
      </div>
    </section>
  );
}
